/* @flow */

import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';

import ArrowRight from 'dibs-icons/exports/ArrowRight';
import ArrowLeft from 'dibs-icons/exports/ArrowLeft';

import getter from 'dibs-object-getter';
import pagelinks from 'dibs-pagelinks';
import classnames from 'classnames';

import css from './Pagination.scss';

const root = typeof window !== 'undefined' ? window : global;

type PaginationState = {
    showPageInputStart: boolean,
    showPageInputEnd: boolean,
};

type PaginationProps = {
    page: number,
    totalResults: number,
    numberOfPages: number,
    handlePaginate: (page: number) => void,
    href?: (page: number) => string,
    pageTilesToDisplay: ?number, // this value should always be even, or else a decimal value will be generated on mobile
};

type Page = {
    type: string,
    key: string,
    pageNumber: number,
    isSelected: boolean,
};

const getInitialState = () => ({
    showPageInputStart: false,
    showPageInputEnd: false,
});

const PreviousPageLink = () => (
    <div className={css.arrowWrapper}>
        <div className={css.arrow}>
            <ArrowLeft />
        </div>
        <div className={css.arrowText}>
            <FormattedMessage id="abt.Pagination.previous" defaultMessage="Previous" />
        </div>
    </div>
);

const NextPageLink = () => (
    <div className={css.arrowWrapper}>
        <div className={css.arrowText}>
            <FormattedMessage id="abt.Pagination.next" defaultMessage="Next" />
        </div>
        <div className={css.arrow}>
            <ArrowRight />
        </div>
    </div>
);

export class Pagination extends Component<PaginationProps, PaginationState> {
    static defaultProps = {
        pageTilesToDisplay: 6,
    };

    constructor(props: PaginationProps) {
        super(props);
        this.state = getInitialState();
    }

    getPageInputStateKey = (isStart: boolean) => {
        // there can be two page input toggles
        return isStart ? 'showPageInputStart' : 'showPageInputEnd';
    };

    togglePageInput = (isStart: boolean) => {
        const stateKey = this.getPageInputStateKey(isStart);
        this.setState(prevState => ({ [stateKey]: !prevState[stateKey] }));
    };

    onPageInputKeyPress = ({ currentTarget, key }: SyntheticKeyboardEvent<HTMLInputElement>) => {
        const numberOfPages = getter(this.props).value('numberOfPages') || 1;
        let pageInput = parseInt(currentTarget.value, 10);
        const isValidPage = pageInput && pageInput > 0;
        if (pageInput > numberOfPages) {
            pageInput = numberOfPages; // input too high, show last page
        }
        if (isValidPage && key === 'Enter') {
            this.props.handlePaginate(pageInput);
            this.setState(getInitialState);
        }
    };

    getPageLink = ({ isSelected, type, key, pageNumber }: Page) => {
        const { handlePaginate, href, page } = this.props;
        const generateHref = href
            ? pageNum => {
                  const [path, params] = href(pageNum).split('?');
                  const searchParams = new URLSearchParams(params || '');
                  const notInteger =
                      typeof pageNumber !== 'undefined' && !Number.isInteger(pageNumber);
                  if (notInteger) {
                      searchParams.append('halfNavPage', true);
                      searchParams.append('windowWidth', root?.innerWidth);
                      searchParams.append('generatedOn', encodeURIComponent(root?.location?.href));
                  }
                  const newParams = searchParams.toString();
                  const showQM = newParams.length;
                  return `${path}${showQM ? '?' : ''}${newParams}`;
              }
            : null;

        switch (type) {
            case 'page': {
                const pageClasses = classnames(css.paginationItem, {
                    [css.activeItem]: isSelected,
                });
                return (
                    <li key={key} className={pageClasses}>
                        {generateHref ? (
                            <a
                                href={generateHref(pageNumber)}
                                onClick={e => {
                                    e.preventDefault();
                                    handlePaginate(pageNumber);
                                }}
                                className={css.page}
                                data-tn={`pagination-page-${pageNumber}`}
                            >
                                {/* converted to string to handle enzyme bug */}
                                {'' + pageNumber}
                            </a>
                        ) : (
                            <a
                                onClick={() => handlePaginate(pageNumber)}
                                className={css.page}
                                data-tn={`pagination-page-${pageNumber}`}
                            >
                                {/* converted to string to handle enzyme bug */}
                                {'' + pageNumber}
                            </a>
                        )}
                    </li>
                );
            }
            case 'back':
                return (
                    <li key={key} className={css.prevArrowItem}>
                        {generateHref ? (
                            <a
                                href={generateHref(page - 1)}
                                onClick={e => {
                                    e.preventDefault();
                                    handlePaginate(page - 1);
                                }}
                                className={css.arrowLink}
                                data-tn="pagination-back"
                            >
                                <PreviousPageLink />
                            </a>
                        ) : (
                            <a
                                onClick={() => handlePaginate(page - 1)}
                                className={css.arrowLink}
                                data-tn="pagination-back"
                            >
                                <PreviousPageLink />
                            </a>
                        )}
                    </li>
                );
            case 'forward':
                return (
                    <li key={key} className={css.nextArrowItem}>
                        {generateHref ? (
                            <a
                                href={generateHref(page + 1)}
                                onClick={e => {
                                    e.preventDefault();
                                    handlePaginate(page + 1);
                                }}
                                className={css.arrowLink}
                                data-tn="pagination-next"
                            >
                                <NextPageLink />
                            </a>
                        ) : (
                            <a
                                onClick={() => handlePaginate(page + 1)}
                                className={css.arrowLink}
                                data-tn="pagination-next"
                            >
                                <NextPageLink />
                            </a>
                        )}
                    </li>
                );
            case 'ellipsis': {
                const isStart = key === 'start-ellipsis';
                const showPageInput = this.state[this.getPageInputStateKey(isStart)];
                return (
                    <li key={key} className={css.ellipsisContainer}>
                        <a onClick={() => this.togglePageInput(isStart)} className={css.ellipsis}>
                            <span>&hellip;</span>
                        </a>
                        {showPageInput && (
                            <div className={css.pageInputContainer} data-tn="pagination-ellipsis">
                                <input
                                    className={css.pageInput}
                                    type="text"
                                    placeholder="page"
                                    onKeyPress={this.onPageInputKeyPress}
                                />
                            </div>
                        )}
                    </li>
                );
            }
            default:
                return false;
        }
    };

    render() {
        const { numberOfPages, page, pageTilesToDisplay } = this.props;
        const pages = pagelinks(numberOfPages, page, pageTilesToDisplay);

        return (
            <div className={css.paginationContainer} data-tn="pagination-wrapper">
                <ul className={css.pageList}>{pages.map(pageEl => this.getPageLink(pageEl))}</ul>
            </div>
        );
    }
}
